'use client'

import { motion } from 'framer-motion'
import { Bot, Users, FileCheck, TrendingUp, Clock, CheckCircle, AlertCircle } from 'lucide-react'

export default function HospitalPortal() {
  return (
    <section id="hospitals" className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            For <span className="text-gradient bg-gradient-to-r from-accent-400 to-primary-400">Hospitals</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Your automated staffing department. Fill critical positions in hours,
            not weeks. Pay fair rates, get better talent.
          </p>
        </motion.div>

        {/* AI Admin Assistant Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-effect rounded-3xl p-8 mb-8"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-3 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl">
              <Bot className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">AI Admin Assistant</h3>
              <p className="text-gray-400">Your 24/7 automated staffing solution</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-effect rounded-2xl p-6"
            >
              <Users className="w-8 h-8 text-cyan-400 mb-4" />
              <h4 className="text-lg font-semibold text-white mb-2">Auto-Matching</h4>
              <p className="text-gray-400 text-sm">
                AI instantly matches your needs with qualified nurses. No manual resume screening.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-effect rounded-2xl p-6"
            >
              <FileCheck className="w-8 h-8 text-green-400 mb-4" />
              <h4 className="text-lg font-semibold text-white mb-2">Instant Credentialing</h4>
              <p className="text-gray-400 text-sm">
                Automated verification and compliance. Nurses ready to start in 2-3 hours.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass-effect rounded-2xl p-6"
            >
              <Clock className="w-8 h-8 text-purple-400 mb-4" />
              <h4 className="text-lg font-semibold text-white mb-2">Smart Scheduling</h4>
              <p className="text-gray-400 text-sm">
                AI handles schedules, call-offs, and replacements automatically.
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* Dashboard Stats */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Current Staffing Status */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass-effect rounded-3xl p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">Staffing Status</h3>
              <div className="px-3 py-1 bg-green-500/20 rounded-full text-sm text-green-400 font-semibold">
                98% Filled
              </div>
            </div>

            <div className="space-y-4">
              {[
                { dept: 'ICU', filled: 24, total: 25, status: 'good' },
                { dept: 'Emergency', filled: 18, total: 18, status: 'perfect' },
                { dept: 'Med-Surg', filled: 31, total: 35, status: 'warning' },
                { dept: 'Pediatrics', filled: 15, total: 15, status: 'perfect' },
              ].map((dept, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-effect rounded-xl p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${
                        dept.status === 'perfect' ? 'bg-green-400' :
                        dept.status === 'good' ? 'bg-cyan-400' :
                        'bg-yellow-400'
                      }`} />
                      <span className="text-white font-semibold">{dept.dept}</span>
                    </div>
                    <span className="text-gray-400 text-sm">
                      {dept.filled}/{dept.total}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                    <motion.div
                      className={`h-full ${
                        dept.status === 'perfect' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                        dept.status === 'good' ? 'bg-gradient-to-r from-cyan-500 to-blue-500' :
                        'bg-gradient-to-r from-yellow-500 to-orange-500'
                      }`}
                      initial={{ width: 0 }}
                      whileInView={{ width: `${(dept.filled / dept.total) * 100}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.3 + index * 0.1 }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.button
              className="w-full mt-6 px-4 py-3 bg-gradient-to-r from-accent-500 to-primary-500 rounded-xl text-white font-semibold"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Post New Position
            </motion.button>
          </motion.div>

          {/* Recent Activity & Savings */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Cost Savings */}
            <div className="glass-effect rounded-3xl p-8 bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30">
              <div className="flex items-center space-x-3 mb-4">
                <TrendingUp className="w-8 h-8 text-green-400" />
                <h3 className="text-2xl font-bold text-white">Cost Savings</h3>
              </div>
              <div className="text-5xl font-bold text-gradient bg-gradient-to-r from-green-400 to-emerald-400 mb-2">
                $48,250
              </div>
              <p className="text-gray-300 mb-4">Saved this quarter vs traditional agencies</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-400">Avg. Cost per Nurse</div>
                  <div className="text-white font-semibold">$108/hr</div>
                </div>
                <div>
                  <div className="text-gray-400">vs Industry Avg.</div>
                  <div className="text-green-400 font-semibold">-18%</div>
                </div>
              </div>
            </div>

            {/* Recent Placements */}
            <div className="glass-effect rounded-3xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Recent Placements</h3>
              <div className="space-y-3">
                {[
                  { name: 'Sarah Johnson, RN', specialty: 'ICU', time: '2 hours ago', status: 'active' },
                  { name: 'Michael Chen, RN', specialty: 'ER', time: '5 hours ago', status: 'active' },
                  { name: 'Emily Rodriguez, RN', specialty: 'Pediatrics', time: '1 day ago', status: 'credentialing' },
                ].map((placement, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center justify-between p-3 glass-effect rounded-xl"
                  >
                    <div className="flex items-center space-x-3">
                      {placement.status === 'active' ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-yellow-400" />
                      )}
                      <div>
                        <div className="text-white font-semibold text-sm">{placement.name}</div>
                        <div className="text-gray-400 text-xs">{placement.specialty}</div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">{placement.time}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
